import { useContext, useMemo } from 'react';

import { NO_TEAR_OUT } from '@common/constants';
import { TearOutModel } from '@common/models';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useOptionalItems } from '@ee-api/optional-items-api/useOptionalItems';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { DrawingShapeContext } from '@ee-context';
import { useAppSelector } from '@redux/hooks';
import { selectSelectedTearOuts } from '@redux/slices/tearOutSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

interface UseTearOutCountertopReturn {
    areTearOutsAvailable: () => boolean;
    getTearOutByProductId: (productId: number) => TearOutModel | undefined;
    filteredSelectedTearOuts: TearOutModel[];
}

const useTearOutCountertop = (): UseTearOutCountertopReturn => {
    const { selectedValue: selectedStore } = useSelectedStore();

    const selectedTearOuts = useAppSelector(selectSelectedTearOuts);
    const { tearOuts } = useOptionalItems();

    const zipcode = useAppSelector(selectZipcode);
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { areThereShapesInTheDrawingArea } = useContext(DrawingShapeContext);

    const areTearOutsAvailable = (): boolean => {
        return Boolean(zipcode && selectedStore && selectedMaterial && areThereShapesInTheDrawingArea);
    };

    const getTearOutByProductId = (productId: number): TearOutModel | undefined => {
        return tearOuts.find((tearOut) => tearOut.productId === productId);
    };

    const filteredSelectedTearOuts: TearOutModel[] = useMemo(() => {
        return [...selectedTearOuts.keys()].reduce((acc, selectedTearOutId) => {
            const selectedTearOut = getTearOutByProductId(selectedTearOutId);
            if (selectedTearOut && selectedTearOut?.name !== NO_TEAR_OUT) {
                return [...acc, selectedTearOut];
            }
            return acc;
        }, [] as TearOutModel[]);
    }, [getTearOutByProductId, selectedTearOuts]);

    return {
        areTearOutsAvailable,
        getTearOutByProductId,
        filteredSelectedTearOuts,
    };
};

export default useTearOutCountertop;
